import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { AlertTriangle, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ROUTE_PATHS } from '../../../app/router/route-paths';
import { useAuth } from '../../../shared/auth/use-auth';
import { formatAppVersion, getDeployedAppVersion } from '../../../shared/config/app-version';
import { collectBugContext } from '../../../shared/diagnostics/bug-context';
import { userFacingActionError } from '../../../shared/lib/staff-user-facing-error';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { ModalDialog } from '../../../shared/ui/ModalDialog';
import { fetchBugRestatement, postBugReport, uploadBugScreenshot } from '../api/bug-report.api';
import { captureBugScreenshot } from '../lib/screenshot';
import type { BugReportOpenRequest } from '../model/bug-report-context';

const MAX_LEN = 2000;

type Step = 'form' | 'restate' | 'success';
export type TicketKind = 'bug' | 'improvement' | 'support' | 'anonymous';

export function BugReportDialog({
  open,
  request,
  onClose
}: {
  open: boolean;
  request: BugReportOpenRequest | null;
  onClose: () => void;
}) {
  const formId = useId();
  const { apiClient } = useAuth();
  const [step, setStep] = useState<Step>('form');
  const [kind, setKind] = useState<TicketKind>('bug');
  const [title, setTitle] = useState('');
  const [whatWrong, setWhatWrong] = useState('');
  const [expected, setExpected] = useState('');
  const [restatement, setRestatement] = useState('');
  const [attachScreenshot, setAttachScreenshot] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);

  const source = request?.source ?? 'dock';
  const conversationId = request?.conversationId ?? null;
  const planId = request?.planId ?? null;

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setKind(request?.initialKind ?? 'bug');
    setTitle('');
    setWhatWrong('');
    setExpected('');
    setRestatement('');
    setAttachScreenshot(false);
    setBusy(false);
    setError(null);
    setCreatedId(null);
  }, [open, request?.nonce, request?.initialKind]);

  const labels = useMemo(() => {
    switch (kind) {
      case 'improvement':
        return {
          title: 'Предложение по улучшению',
          desc: 'Опишите задачу и как её лучше реализовать с вашей точки зрения. Контекст приложится автоматически.',
          field1: 'Какую задачу мы решаем',
          hint1: 'Опишите проблему, неудобство или задачу, с которой сталкиваетесь вы или клиенты...',
          field2: 'Как это лучше реализовать с точки зрения пользователя',
          hint2: 'Предложите удобный сценарий, поведение кнопок, экрана или процесса...',
        };
      case 'support':
        return {
          title: 'Вопрос или техподдержка',
          desc: 'Задайте вопрос по работе системы или обратитесь за поддержкой разработчиков.',
          field1: 'Суть вопроса / обращения',
          hint1: 'Опишите подробно, с чем возникли сложности или какой у вас вопрос...',
          field2: 'Ожидаемый результат или детали',
          hint2: 'Укажите важные детали или желаемый результат...',
        };
      case 'anonymous':
        return {
          title: 'Анонимный отзыв',
          desc: 'Ваше обращение уйдёт разработчикам платформы без указания имени, роли и email.',
          field1: 'Текст отзыва / обращения',
          hint1: 'Напишите ваши впечатления, пожелания или замечания...',
          field2: 'Дополнительные предложения',
          hint2: 'Что помогло бы сделать работу в системе приятнее и удобнее?...',
        };
      case 'bug':
      default:
        return {
          title: 'Сообщить об ошибке',
          desc: 'Опишите своими словами: что пошло не так и как должно быть. Контекст приложится автоматически.',
          field1: 'Что пошло не так',
          hint1: 'Например: не сохраняется запись клиента, появляется ошибка',
          field2: 'Как должно быть',
          hint2: 'Например: запись сохраняется, клиент появляется в расписании',
        };
    }
  }, [kind]);

  const contextPreview = useMemo(
    () =>
      collectBugContext({
        assistant: { conversation_id: conversationId, plan_id: planId }
      }),
    [conversationId, planId]
  );

  function validate(): boolean {
    if (!whatWrong.trim()) {
      setError(`Заполните поле «${labels.field1}»`);
      return false;
    }
    if (!expected.trim()) {
      setError(`Заполните поле «${labels.field2}»`);
      return false;
    }
    if (whatWrong.length > MAX_LEN || expected.length > MAX_LEN) {
      setError(`Не более ${MAX_LEN} символов в каждом поле`);
      return false;
    }
    setError(null);
    return true;
  }

  async function send(aiRestatement?: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      let screenshotKey: string | undefined;
      if (attachScreenshot && kind !== 'anonymous') {
        const blob = await captureBugScreenshot();
        screenshotKey = await uploadBugScreenshot(apiClient, blob);
      }
      const created = await postBugReport(apiClient, {
        title: title.trim() || undefined,
        ticket_kind: kind,
        what_wrong: whatWrong.trim(),
        expected: expected.trim(),
        ...(aiRestatement ? { ai_restatement: aiRestatement, restatement_confirmed: true } : {}),
        source: source as 'dock' | 'assistant' | 'portal',
        conversation_id: conversationId,
        plan_id: planId,
        context: collectBugContext({ assistant: { conversation_id: conversationId, plan_id: planId } }),
        ...(screenshotKey ? { screenshot_media_key: screenshotKey } : {})
      });
      setCreatedId(created.id);
      setStep('success');
    } catch (err) {
      setError(userFacingActionError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onNext(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetchBugRestatement(apiClient, {
        what_wrong: whatWrong.trim(),
        expected: expected.trim(),
        route: `${window.location.pathname}`
      });
      if (res.available && res.restatement.trim()) {
        setRestatement(res.restatement.trim());
        if (!title.trim() && res.title?.trim()) {
          setTitle(res.title.trim());
        }
        setStep('restate');
        setBusy(false);
        return;
      }
    } catch {
      // ИИ недоступен — шаг пересказа молча пропускаем (см. спеку).
    }
    setBusy(false);
    await send();
  }

  const footer =
    step === 'form' ? (
      <>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Отменить
        </Button>
        <Button type="submit" form={formId} disabled={busy}>
          {busy ? 'Проверяем…' : 'Далее'}
        </Button>
      </>
    ) : step === 'restate' ? (
      <>
        <Button type="button" variant="secondary" onClick={() => setStep('form')} disabled={busy}>
          Поправить
        </Button>
        <Button type="button" onClick={() => void send(restatement)} disabled={busy}>
          {busy ? 'Отправляем…' : 'Верно, отправить'}
        </Button>
      </>
    ) : (
      <>
        <Button type="button" variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
        <Link
          to={`${ROUTE_PATHS.feedback}?ticket=${createdId ?? ''}`}
          onClick={onClose}
          className="inline-flex h-9 items-center justify-center rounded-sm bg-[hsl(var(--ui-primary))] px-3 text-sm font-medium text-[hsl(var(--ui-primary-foreground))]"
        >
          Перейти к обращению
        </Link>
      </>
    );

  const appVersion = getDeployedAppVersion();
  return (
    <ModalDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={labels.title}
      description={
        step === 'success'
          ? 'Обращение ушло разработчикам платформы.'
          : labels.desc
      }
      className="max-h-[min(90vh,900px)] overflow-y-auto sm:max-w-xl"
      dataTestId="bug-report-dialog"
      footer={footer}
    >
      <p
        className="mb-3 text-xs text-[hsl(var(--ui-muted-foreground))]"
        data-testid="bug-report-version"
        title={appVersion.commit ?? undefined}
      >
        Версия: <span className="font-mono">{formatAppVersion(appVersion)}</span>
      </p>
      {step === 'success' ? (
        <div className="space-y-3" data-testid="bug-report-success">
          <p className="text-base font-semibold">Обращение №{createdId} отправлено</p>
          <p className="text-sm text-[hsl(var(--ui-muted-foreground))]">
            Статус и переписку можно смотреть в разделе «Клиентский портал».
          </p>
        </div>
      ) : step === 'restate' ? (
        <div className="space-y-3" data-testid="bug-report-restate">
          <Field id="bug-restate-title" label="Заголовок обращения">
            <input
              id="bug-restate-title"
              type="text"
              className="w-full rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Краткая суть обращения"
            />
          </Field>
          <div>
            <span className="text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">Пересказ ИИ для разработчиков:</span>
            <p className="mt-1 rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted))] p-3 text-sm">
              {restatement}
            </p>
          </div>
          <p className="text-xs text-[hsl(var(--ui-muted-foreground))]">
            Если пересказ неточный — нажмите «Поправить» и измените текст.
          </p>
        </div>
      ) : (
        <form id={formId} className="space-y-4" onSubmit={(e) => void onNext(e)} data-testid="bug-report-form">
          <div className="flex flex-wrap gap-1 rounded-md border border-[hsl(var(--ui-border))] p-1 bg-[hsl(var(--ui-muted))] text-xs">
            <button
              type="button"
              className={`flex-1 rounded px-2.5 py-1.5 font-medium transition-colors ${kind === 'bug' ? 'bg-[hsl(var(--ui-card))] text-[hsl(var(--ui-foreground))] shadow-xs' : 'text-[hsl(var(--ui-muted-foreground))] hover:text-[hsl(var(--ui-foreground))]'}`}
              onClick={() => setKind('bug')}
            >
              Ошибка
            </button>
            <button
              type="button"
              className={`flex-1 rounded px-2.5 py-1.5 font-medium transition-colors ${kind === 'improvement' ? 'bg-[hsl(var(--ui-card))] text-[hsl(var(--ui-foreground))] shadow-xs' : 'text-[hsl(var(--ui-muted-foreground))] hover:text-[hsl(var(--ui-foreground))]'}`}
              onClick={() => setKind('improvement')}
            >
              Улучшение
            </button>
            <button
              type="button"
              className={`flex-1 rounded px-2.5 py-1.5 font-medium transition-colors ${kind === 'support' ? 'bg-[hsl(var(--ui-card))] text-[hsl(var(--ui-foreground))] shadow-xs' : 'text-[hsl(var(--ui-muted-foreground))] hover:text-[hsl(var(--ui-foreground))]'}`}
              onClick={() => setKind('support')}
            >
              Поддержка
            </button>
            <button
              type="button"
              className={`flex-1 rounded px-2.5 py-1.5 font-medium transition-colors ${kind === 'anonymous' ? 'bg-[hsl(var(--ui-card))] text-[hsl(var(--ui-foreground))] shadow-xs' : 'text-[hsl(var(--ui-muted-foreground))] hover:text-[hsl(var(--ui-foreground))]'}`}
              onClick={() => setKind('anonymous')}
            >
              Анонимно
            </button>
          </div>

          <Field id="bug-title" label="Заголовок" hint="(необязательно — сформулирует ИИ)">
            <input
              id="bug-title"
              type="text"
              className="w-full rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Краткая суть обращения"
            />
          </Field>

          <Field id="bug-what-wrong" label={labels.field1} hint={`1–${MAX_LEN} символов`}>
            <textarea
              id="bug-what-wrong"
              className="min-h-24 w-full rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
              value={whatWrong}
              maxLength={MAX_LEN}
              onChange={(e) => setWhatWrong(e.target.value)}
              placeholder={labels.hint1}
              aria-label={labels.field1}
            />
          </Field>

          <Field id="bug-expected" label={labels.field2} hint={`1–${MAX_LEN} символов`}>
            <textarea
              id="bug-expected"
              className="min-h-24 w-full rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
              value={expected}
              maxLength={MAX_LEN}
              onChange={(e) => setExpected(e.target.value)}
              placeholder={labels.hint2}
              aria-label={labels.field2}
            />
          </Field>

          {kind !== 'anonymous' ? (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={attachScreenshot}
                onChange={(e) => setAttachScreenshot(e.target.checked)}
                data-testid="bug-report-screenshot-checkbox"
              />
              <span>
                <span className="inline-flex items-center gap-1 font-medium">
                  <ImageIcon className="h-4 w-4" aria-hidden />
                  Приложить снимок экрана
                </span>
                <span className="block text-xs text-[hsl(var(--ui-muted-foreground))]">
                  По умолчанию выключено: на экране могут быть персональные данные клиентов.
                </span>
              </span>
            </label>
          ) : null}

          <details className="rounded-sm border border-[hsl(var(--ui-border))] p-2 text-xs">
            <summary className="flex cursor-pointer items-center gap-1 font-medium">
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              Техническая информация
              <span className="font-normal text-[hsl(var(--ui-muted-foreground))]">— будет приложена к {kind === 'bug' ? 'отчёту об ошибке' : 'обращению'}</span>
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] text-[hsl(var(--ui-muted-foreground))]">
              {JSON.stringify(contextPreview, null, 2)}
            </pre>
          </details>
          {error ? (
            <p className="flex items-start gap-1 text-sm text-red-600" role="alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {error}
            </p>
          ) : null}
        </form>
      )}
    </ModalDialog>
  );
}
